<div class="sidebar-context px-3 position-relative" @keydown.esc="open && toggle()">

  <!-- SELECTED CONTEXT CARD (DROPDOWN TOGGLE) -->
  <button
    type="button"
    class="sidebar-context-card context-card btn w-100 text-start border border-danger-subtle rounded-3 p-2 bg-danger-subtle"
    :aria-expanded="open"
    aria-haspopup="listbox"
    :title="main_label"
    @click="toggle"
  >
    <span class="sidebar-context-card-inner d-flex align-items-center gap-2">
      <span class="sidebar-context-copy d-flex align-items-center gap-2 flex-grow-1 min-w-0">
        <span class="d-inline-flex flex-shrink-0 p-2 lh-1 rounded-2 bg-white text-danger shadow-sm">
          <i :class="icon"></i>
        </span>

        <span class="flex-grow-1 min-w-0">
          <span class="d-block text-dark fw-bold text-xs text-truncate">{{ main_label }}</span>
          <span class="d-block text-muted text-xxs text-uppercase text-truncate">{{ sub_label }}</span>
        </span>
      </span>

      <i class="sidebar-context-toggle bi" :class="open ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
    </span>
  </button>

  <!-- DROPDOWN PANEL -->
  <div
    v-if="open"
    class="sidebar-context-dropdown context-dropdown dropdown-panel position-absolute bg-white border border-danger-subtle rounded-4 shadow p-2"
  >

    <!-- ========== LEVEL 1: SECTORS ========== -->
    <div v-if="view === 'sectors'">

      <!-- OVERVIEW DASHBOARD ENTRY -->
      <div v-if="top_label">
        <p class="text-muted text-uppercase fw-semibold text-xxs px-2 mb-2">Overview</p>

        <button
          type="button"
          class="sidebar-context-option list-group-item list-group-item-action d-flex align-items-center gap-2 w-100 border-0 rounded-3"
          :class="{ 'bg-danger-subtle': top_active }"
          @click="selectTop"
        >
          <span class="bi bi-grid-1x2-fill text-danger flex-shrink-0"></span>

          <span class="flex-grow-1 min-w-0">
            <span class="d-block fw-semibold text-xs text-truncate" :class="top_active ? 'text-danger' : 'text-dark'">{{ top_label }}</span>
            <span class="d-block text-muted text-xxs text-truncate">{{ top_subtitle }}</span>
          </span>

          <span class="bi flex-shrink-0" :class="top_active ? 'bi-check2 text-danger' : 'bi-chevron-right text-muted'"></span>
        </button>

        <hr class="border-danger-subtle opacity-100 my-2">
      </div>

      <div class="d-flex align-items-center justify-content-between px-2 mb-2">
        <span class="text-muted text-uppercase fw-semibold text-xxs">Sectors</span>
        <span class="badge rounded-pill bg-light text-secondary border text-xxs">{{ sectors.length }}</span>
      </div>

      <div class="list-group d-flex flex-column gap-1" role="listbox" aria-label="Sectors">
        <button
          v-for="sector in sectors"
          :key="sector.id"
          type="button"
          role="option"
          class="sidebar-context-option list-group-item list-group-item-action d-flex align-items-center gap-2 w-100 border-0 rounded-3"
          :class="{ 'bg-danger-subtle': isActiveSector(sector), 'opacity-50': !sector.available }"
          :disabled="!sector.available"
          :aria-selected="isActiveSector(sector)"
          :title="sector.label"
          @click="selectSector(sector)"
        >
          <span
            class="flex-shrink-0"
            :class="[sector.icon, isActiveSector(sector) ? 'text-danger' : 'text-secondary']"
          ></span>

          <span class="flex-grow-1 min-w-0">
            <span class="d-block fw-semibold text-xs text-truncate" :class="isActiveSector(sector) ? 'text-danger' : 'text-dark'">
              {{ sector.label }}
            </span>
            <span class="d-block text-muted text-xxs text-truncate">
              {{ isActiveSector(sector) && items.length ? `${items.length} ${items.length === 1 ? 'site' : 'sites'}` : sector.description }}
            </span>
          </span>

          <span
            v-if="isActiveSector(sector) && !items.length"
            class="badge rounded-pill bg-danger-subtle text-danger text-xxs flex-shrink-0"
          >
            Current
          </span>
          <span
            v-else-if="!sector.available"
            class="badge rounded-pill bg-light text-secondary border text-xxs flex-shrink-0"
          >
            Soon
          </span>
          <span v-else class="bi bi-chevron-right text-muted flex-shrink-0"></span>
        </button>
      </div>
    </div>

    <!-- ========== LEVEL 2: SECTOR ITEMS ========== -->
    <div v-else>

    <!-- BACK TO SECTORS -->
    <button
      v-if="sectors.length"
      type="button"
      class="btn btn-sm btn-link text-decoration-none text-muted d-flex align-items-center gap-2 w-100 px-2 py-1 mb-1 text-xs"
      @click="view = 'sectors'"
    >
      <span class="bi bi-arrow-left"></span>
      <span class="text-truncate">All sectors</span>
      <span v-if="current_sector" class="ms-auto badge rounded-pill bg-danger-subtle text-danger text-xxs">
        {{ current_sector.label }}
      </span>
    </button>

    <!-- PORTFOLIO ENTRY (only when there is no sector level to show it on) -->
    <div v-if="top_label && !sectors.length">
      <button
        type="button"
        class="sidebar-context-option list-group-item list-group-item-action d-flex align-items-center gap-2 w-100 border-0 rounded-3"
        :class="{ 'bg-danger-subtle': top_active }"
        @click="selectTop"
      >
        <span class="bi bi-grid-1x2-fill text-danger flex-shrink-0"></span>

        <span class="flex-grow-1 min-w-0">
          <span class="d-block fw-semibold text-xs text-truncate" :class="top_active ? 'text-danger' : 'text-dark'">{{ top_label }}</span>
          <span class="d-block text-muted text-xxs text-truncate">{{ top_subtitle }}</span>
        </span>

        <span class="bi flex-shrink-0" :class="top_active ? 'bi-check2 text-danger' : 'bi-chevron-right text-muted'"></span>
      </button>

      <hr class="border-danger-subtle opacity-100 my-2">
    </div>

    <!-- SECTION HEADER -->
    <div class="d-flex align-items-center justify-content-between px-2 mb-2">
      <span class="text-muted text-uppercase fw-semibold text-xxs">Properties</span>
      <span class="badge rounded-pill bg-light text-secondary border text-xxs">{{ items.length }}</span>
    </div>

    <!-- SEARCH -->
    <div v-if="items.length > search_threshold" class="input-group px-1 mb-2">
      <span class="input-group-text bg-white border-end-0 text-muted text-xs py-1">
        <span class="bi bi-search"></span>
      </span>
      <input
        ref="search_input"
        v-model.trim="search"
        type="search"
        class="form-control border-start-0 ps-0 py-1 text-xs shadow-none"
        placeholder="Search properties"
        aria-label="Search properties"
      >
    </div>

    <!-- PROPERTY LIST -->
    <div class="dropdown-scroll list-group d-flex flex-column gap-1 overflow-auto" role="listbox">
      <button
        v-for="item in filteredItems"
        :key="item.id"
        type="button"
        role="option"
        class="sidebar-context-option list-group-item list-group-item-action d-flex align-items-center gap-2 w-100 border-0 rounded-3"
        :class="{ 'bg-danger-subtle': isActive(item) }"
        :aria-selected="isActive(item)"
        :title="item.label"
        @click="select(item)"
      >
        <span
          class="flex-shrink-0"
          :class="[item.icon || 'bi bi-building', isActive(item) ? 'text-danger' : 'text-secondary']"
        ></span>

        <span class="flex-grow-1 min-w-0">
          <span class="d-block fw-semibold text-xs text-truncate" :class="isActive(item) ? 'text-danger' : 'text-dark'">
            {{ item.label }}
          </span>
          <span v-if="item.description" class="d-block text-muted text-xxs text-truncate">{{ item.description }}</span>
        </span>

        <span
          v-if="isLive(item)"
          class="badge rounded-pill bg-success-subtle text-success-emphasis border border-success-subtle d-inline-flex align-items-center gap-1 text-xxs flex-shrink-0"
        >
          <span class="d-inline-block p-1 rounded-circle bg-success"></span>
          Live
        </span>

        <span v-if="isActive(item)" class="bi bi-check2 text-danger flex-shrink-0"></span>
      </button>

      <p v-if="!filteredItems.length" class="text-muted text-xs text-center py-3 mb-0">
        No properties match "{{ search }}"
      </p>
    </div>

    </div>
  </div>

</div>
